import React, { useState } from "react";

/**
 * PlatformChips — compact, link-or-copy chips for an operator's gaming
 * platform handles.
 *
 * Inputs accepted (any subset; missing/empty handles are skipped):
 *   - steam (vanity ID), xbox (Gamertag), psn (PSN ID), epic, discord
 *
 * `compact` — render smaller chips (used on the LFG card).
 * `showEmpty` — when true and no handles are set, render the empty-state
 *               nudge instead of returning null (used on the operator's
 *               own profile/dashboard so they see the CTA).
 * `onCopy` — toast handler called when a copy-only handle is clicked.
 *
 * Each chip uses a small inline SVG glyph — NEVER an emoji icon — and
 * uses LEAGUE HUB's teal accent (#14b8a6) for the border on hover.
 *
 * Platforms with a public profile URL render as <a target="_blank">;
 * PSN/Epic/Discord have no reliable public page, so they copy the
 * handle to the clipboard on click.
 */

// SVG glyphs — sourced as one-path/simple-shape silhouettes so they
// inherit `currentColor` and stay crisp at small sizes. NOT emoji.
const GLYPHS = {
  steam: (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M12 2C6.5 2 2 6.5 2 12c0 4.5 3 8.3 7.1 9.6L11.4 18a2.6 2.6 0 0 1-.2-1c0-.1 0-.3.1-.4l-3-1.2.5-.2c1.5-.6 3.2.1 3.8 1.5l3.7-2.7a2.7 2.7 0 1 1 5.4-.1A10 10 0 0 0 22 12c0-5.5-4.5-10-10-10Zm6 11.4a1.6 1.6 0 1 1-3.2 0 1.6 1.6 0 0 1 3.2 0Zm-7.1 4-1.4-.6c.4.7 1.1 1.2 2 1.2.7 0 1.4-.4 1.7-.9-.5.4-1.5.6-2.3.3Z" />
    </svg>
  ),
  xbox: (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M12 2a10 10 0 0 0-6.8 2.7c1.7-.4 4.2.5 6.8 3.1 2.6-2.6 5.1-3.5 6.8-3.1A10 10 0 0 0 12 2Zm8.7 5.4c-1.5-1-3.6.4-6 3.2 3 3.7 4.4 6.9 3.5 8.6A10 10 0 0 0 22 12a10 10 0 0 0-1.3-4.6Zm-17.4 0A10 10 0 0 0 2 12a10 10 0 0 0 3.8 7.2c-.9-1.7.5-4.9 3.5-8.6-2.4-2.8-4.5-4.2-6-3.2ZM12 9.6c-3.5 4.1-5.4 8.4-4 10.2A10 10 0 0 0 12 22a10 10 0 0 0 4-1.2c1.4-1.8-.5-6.1-4-10.2Z" />
    </svg>
  ),
  psn: (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M9.8 4.2v14.2l3-.9V8.7c0-.6.3-1 .8-1 .6 0 .9.5.9 1.2v6.5c1.7-.8 3-1.8 3-3.8V8.4c0-2.1-1.7-3.3-4.6-3.7-1.1-.2-2.2-.4-3.1-.5Zm-4 11.9c-2 .6-2.5 1.7-1.2 2.6 1.3.9 4 1.4 6.4.9v-1.7l-3.5 1.1c-.5.1-.6-.1-.2-.3l3.7-1.2v-1.5c-1.8 0-3.7.4-5.2.9v.2Zm10.3 1.3c1.7.6 4 .7 5.4.3 1.4-.4 1.7-1.2.4-1.9-.7-.3-2-.7-3.3-.9l-.1-1.5c1.7.4 3.5.9 4.6 1.4 1.3.5 1.6 1.3 1 2-1.2 1.4-4.5 2.3-7.7 1.7v-1Z" />
    </svg>
  ),
  epic: (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M5 4h14a2 2 0 0 1 2 2v9.4l-1.6.5V7a1 1 0 0 0-1-1H6.5a1 1 0 0 0-1 1v11.7l4.7 1.9 1-.5v-3.5H8.6V15h4v4l-2.3 1.3-7.3-3V6a2 2 0 0 1 2-2Zm3.4 6.3h2.4v3.2H8.4v-3.2Zm0-3.4h7.8v1.2H8.4V6.9Zm5.2 5.2v-1.4h3v1.4h-3Zm0 2.3v-1.2h3v1.2h-3Z" />
    </svg>
  ),
  discord: (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M19.3 5.3a16 16 0 0 0-4-1.2l-.3.4a14.7 14.7 0 0 0-6 0L8.7 4a16 16 0 0 0-4 1.2A18 18 0 0 0 2 14a16 16 0 0 0 4.7 2.4l.9-1.2a10 10 0 0 1-1.7-.8l.4-.3a11.5 11.5 0 0 0 11.4 0l.4.3a10 10 0 0 1-1.7.8l.9 1.2A16 16 0 0 0 22 14a18 18 0 0 0-2.7-8.7ZM9.1 12.7c-.8 0-1.5-.8-1.5-1.7s.7-1.7 1.5-1.7 1.5.8 1.5 1.7-.7 1.7-1.5 1.7Zm5.8 0c-.8 0-1.5-.8-1.5-1.7s.7-1.7 1.5-1.7 1.5.8 1.5 1.7-.7 1.7-1.5 1.7Z" />
    </svg>
  ),
  riot: (
    // League / Riot ID — stylised "R" silhouette in their orange accent.
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M3 3v14h2v-3h2.5l2 3H12l-2.3-3.4A3.4 3.4 0 0 0 11 10.4c0-2-1.6-3.4-3.7-3.4H5V3H3Zm2 5.6h2.3c1 0 1.7.7 1.7 1.7s-.6 1.7-1.7 1.7H5V8.6Zm10 12.4h6v-2h-6v2Zm0-4h6v-2h-6v2Zm0-4h6v-2h-6v2Z" />
    </svg>
  ),
  battlenet: (
    // Battle.net — simplified rune mark.
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10 10-4.5 10-10S17.5 2 12 2Zm0 3a7 7 0 1 1 0 14 7 7 0 0 1 0-14Zm-3 3.5 1.6 3L8 12.6l1.8 1L8.4 17l3.6-3.5L15.6 17l-1.4-3.4 1.8-1-2.6-1.1L15 8.5l-3 1.7-3-1.7Z" />
    </svg>
  ),
};

// Public-profile URL builders. Returning null means the platform
// doesn't have a reliable public page — fall back to copy-to-clipboard.
const URL_BUILDERS = {
  steam:     (h) => `https://steamcommunity.com/id/${encodeURIComponent(h)}`,
  xbox:      (h) => `https://account.xbox.com/profile?gamertag=${encodeURIComponent(h)}`,
  psn:       () => null,
  epic:      () => null,
  discord:   () => null,
  // Riot IDs ("Name#TAG") can be looked up on tracker.gg per-game; we
  // can't disambiguate the game here, so we keep them copy-to-clipboard.
  riot:      () => null,
  battlenet: () => null,
};

const LABELS = {
  steam:     "Steam",
  xbox:      "Xbox",
  psn:       "PSN",
  epic:      "Epic",
  discord:   "Discord",
  riot:      "Riot ID",
  battlenet: "Battle.net",
};

/**
 * Pull the 5 supported platform handles off either a "user-shaped"
 * doc (steam_handle, xbox_handle, …) or an "lfg snapshot" doc
 * (user_platforms.steam, …). Empty strings are filtered out.
 */
function extractHandles(source) {
  if (!source) return {};
  const snap = source.user_platforms || {};
  const handles = {
    steam:     (source.steam_handle     || snap.steam     || "").trim(),
    xbox:      (source.xbox_handle      || snap.xbox      || "").trim(),
    psn:       (source.psn_handle       || snap.psn       || "").trim(),
    epic:      (source.epic_handle      || snap.epic      || "").trim(),
    riot:      (source.riot_handle      || snap.riot      || "").trim(),
    battlenet: (source.battlenet_handle || snap.battlenet || "").trim(),
    discord:   (source.discord_handle   || snap.discord   || "").trim(),
  };
  Object.keys(handles).forEach((k) => { if (!handles[k]) delete handles[k]; });
  return handles;
}

export function PlatformChips({
  source,           // user OR lfg post — auto-detected
  compact = false,
  showEmpty = false,
  onCopy,
  hideDiscord = false, // discord shown elsewhere on the holo card
  testIdPrefix = "platform-chip",
}) {
  const [copied, setCopied] = useState(null);
  const handles = extractHandles(source);
  if (hideDiscord) delete handles.discord;
  const entries = Object.entries(handles);

  if (entries.length === 0) {
    if (!showEmpty) return null;
    return (
      <p
        data-testid="platform-chip-empty"
        data-testid-prefixed={`${testIdPrefix}-empty`}
        className="text-[10px] tracking-wider text-white/35 italic"
      >
        Add your handles so captains can find you.
      </p>
    );
  }

  const sz = compact ? "h-7 px-2 text-[10px]" : "h-8 px-2.5 text-[11px]";
  const iconSz = compact ? "w-3.5 h-3.5" : "w-4 h-4";

  const onChipClick = (platform, handle, url) => async (e) => {
    if (url) return; // <a> already handles the navigation
    e.preventDefault();
    try {
      await navigator.clipboard?.writeText(handle);
      setCopied(platform);
      onCopy?.(`${LABELS[platform]} handle copied: ${handle}`);
      setTimeout(() => setCopied((c) => (c === platform ? null : c)), 1400);
    } catch {
      onCopy?.(`Couldn't copy — handle is: ${handle}`);
    }
  };

  return (
    <div
      data-testid={testIdPrefix}
      className="flex flex-wrap gap-1.5"
      aria-label="Connected gaming platforms"
    >
      {entries.map(([platform, handle]) => {
        const url = URL_BUILDERS[platform]?.(handle);
        const baseClass =
          `${sz} inline-flex items-center gap-1.5 rounded-full ` +
          "bg-black/40 border border-white/10 text-white/85 " +
          "hover:border-[#14b8a6]/60 hover:text-[#14b8a6] " +
          "transition-colors font-medium leading-none";
        const inner = (
          <>
            <span className={`${iconSz} shrink-0`}>{GLYPHS[platform]}</span>
            <span className="truncate max-w-[140px]" title={`${LABELS[platform]}: ${handle}`}>
              {handle}
            </span>
            {copied === platform && (
              <span className="text-[#14b8a6] text-[9px] tracking-widest">COPIED</span>
            )}
          </>
        );
        const tid = `${testIdPrefix}-${platform}`;
        if (url) {
          return (
            <a
              key={platform}
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className={baseClass}
              data-testid={tid}
            >
              {inner}
            </a>
          );
        }
        return (
          <button
            key={platform}
            type="button"
            onClick={onChipClick(platform, handle, null)}
            className={baseClass}
            data-testid={tid}
            title={`Click to copy ${LABELS[platform]} handle`}
          >
            {inner}
          </button>
        );
      })}
    </div>
  );
}

export default PlatformChips;
